import { GeographyApi } from "@App/apis/graphql/geography/interfaces";
import { actionCreators, State, store } from "@App/state";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * A custom hook that checks if all needed data was loaded from the api on component mount
 * and returns a method to parse the data to the url query parameters.
 */
export const usePlaceParameters = (geographyApi: GeographyApi) => {
  const dispatch = useDispatch();
  const { choosePlace } = bindActionCreators(actionCreators, dispatch);
  const [searchParams, setSearchParams] = useSearchParams();
  const { hash } = useLocation();
  const place = useSelector((state: State) => state.orders.place);

  // Get the location query from old HashRouter links.
  if (hash && hash !== "null" && hash !== undefined) {
    // searchParams.set('location', hash.match(/(?<=location=).*(?=&*)/g)[0]) <-- Safari does not support this
    if (hash.includes("location=")) {
      searchParams.set("location", hash.split("location=")[1].split("&")[0]);
    }
  }

  useEffect(() => {
    const placeQuery = searchParams.get("place");
    const locationQuery = searchParams.get("location");
    async function getLocationByString(search: string) {
      try {
        const locations = await geographyApi.getLocationSuggests(search);
        if (locations.length < 1) return;

        const exactLocation = await geographyApi.getLocationByID(locations[0].id);
        choosePlace(exactLocation);
        searchParams.set("location", null);
      } catch (e) {}
    }

    async function getLocationById(id: string) {
      try {
        const exactLocation = await geographyApi.getLocationByID(id);
        choosePlace(exactLocation);
      } catch (e) {}
    }

    if (locationQuery && locationQuery !== "null" && !place && !placeQuery) {
      getLocationByString(locationQuery);
    }

    // get place from api if not in state
    if (placeQuery && (!place || !place.lat || place.id !== placeQuery)) {
      getLocationById(placeQuery);
    }
  }, []);

  // if place changes, automatically update the query parameters
  useEffect(() => {
    if (place && place.id && place.id !== searchParams.get("place")) {
      setSearchParams((searchParams) => {
        searchParams.set("place", place.id);
        return searchParams;
      });
    }
  }, [place]);

  const placeQueryParams = (): Record<string, string> => {
    return {
      // add place if existing
      ...(store.getState().orders.place && {
        place: store.getState().orders.place.id,
      }),
    };
  };

  return { placeQueryParams };
};
